/// <reference types="google.maps" />
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GoogleMap, GoogleMarkerClusterer, InfoWindowF, MarkerF } from '@react-google-maps/api'

import cn from '@components/utils/cn'
import { useMediaQuery } from '@hooks/useMediaQuery'
import { scopedTranslation } from '@utils/I18n'

import type { Coordinates, FoundPlace } from '@utils/embeds/distance'
import { PIN_PATH, spreadPins } from '@utils/embeds/mapPins'
import { useGoogleMaps, useMapFailure } from '@utils/embeds/places'
import { LocationSearch } from './LocationSearch'

const t = scopedTranslation('embeds.map')

export type MapGroup = {
  id: number
  name: string
  url: string
  address: string | null
  excerpt: string | null
  lat: number
  lng: number
}

type GroupPin = { group: MapGroup; position: Coordinates }

// The map's colours and pin from its Bootstrap days ($primary).
const GREEN = 'tw:text-[#007966] tw:hover:text-[#00473c]'
const BUTTON =
  'tw:inline-block tw:rounded tw:border tw:border-[#007966] tw:bg-[#007966] tw:px-3 tw:py-1.5 tw:text-base tw:text-white tw:hover:border-[#005346] tw:hover:bg-[#005346] tw:hover:text-white tw:hover:no-underline'

const PIN_ICON = {
  path: PIN_PATH,
  fillColor: '#007966',
  fillOpacity: 1,
  scale: 1.2,
  strokeColor: '#000000',
  strokeWeight: 2,
}

const START_CENTER = { lat: -29, lng: 137 }
const START_ZOOM = 10
const SINGLE_PIN_ZOOM = 14
const PLACE_ZOOM = 11

function summary({ total, inView }: { total: number; inView: number }) {
  if (total === 0) return t('summary.none')
  if (inView === 0) return t('summary.none_here')
  if (inView === total) return t('summary.all', { count: total })
  return t('summary.some', { groups: t('groups', { count: inView }), count: total })
}

function GroupDetails({ group, withExcerpt }: { group: MapGroup; withExcerpt: boolean }) {
  return (
    <>
      <h2 className="tw:m-0 tw:mb-2 tw:text-2xl tw:font-medium tw:leading-tight">
        <a href={group.url} target="_blank" rel="noopener" className={cn(GREEN, 'tw:hover:underline')}>
          {group.name}
        </a>
      </h2>
      {group.address && <p className="tw:m-0 tw:mb-4 tw:italic">{group.address}</p>}
      {withExcerpt && group.excerpt && <p className="tw:m-0 tw:mb-4">{group.excerpt}</p>}
      <a href={group.url} target="_blank" rel="noopener" className={BUTTON}>
        {t('view_group')}
      </a>
    </>
  )
}

function GroupList({
  groups,
  total,
  onSelect,
  className,
}: {
  groups: MapGroup[]
  total: number
  onSelect?: (id: number) => void
  className?: string
}) {
  return (
    <div className={cn('tw:flex tw:flex-col', className)}>
      <p className="tw:m-0 tw:px-5 tw:pb-2.5 tw:pt-1.5 tw:text-2xl tw:font-medium tw:leading-tight">
        {summary({ total, inView: groups.length })}
      </p>
      <ul className="tw:m-0 tw:list-none tw:p-0">
        {groups.map((group) => (
          // Clicking a listing opens its pin's popup, as it always has; its links stay the keyboard's way in.
          <li
            key={group.id}
            onClick={onSelect && (() => onSelect(group.id))}
            className="tw:border-t tw:border-[#007966] tw:px-5 tw:py-4"
          >
            <GroupDetails group={group} withExcerpt={false} />
          </li>
        ))}
      </ul>
    </div>
  )
}

type MapAndListProps = {
  groups: MapGroup[]
  apiKey: string
  country: string
  onError: () => void
}

function MapAndList({ groups, apiKey, country, onError }: MapAndListProps) {
  const { isLoaded, loadError } = useGoogleMaps({ apiKey, country })
  const stacked = useMediaQuery('(max-width: 639px)')
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [place, setPlace] = useState<FoundPlace | null>(null)
  const [inViewIds, setInViewIds] = useState<Set<number> | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const pins = useMemo<GroupPin[]>(
    () => spreadPins(groups.map((group) => ({ group, position: { lat: group.lat, lng: group.lng } }))),
    [groups]
  )
  const mapOptions = useMemo<google.maps.MapOptions | null>(
    () =>
      isLoaded
        ? {
            scrollwheel: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            // Maps now shows a camera control instead; the map has always had zoom buttons at the top left.
            cameraControl: false,
            zoomControl: true,
            zoomControlOptions: { position: google.maps.ControlPosition.LEFT_TOP },
          }
        : null,
    [isLoaded]
  )
  const selected = pins.find(({ group }) => group.id === selectedId) ?? null
  const inView = inViewIds ? groups.filter(({ id }) => inViewIds.has(id)) : groups

  useMapFailure({ loadError, onError })

  useEffect(() => {
    if (!map || pins.length === 0) return
    if (pins.length === 1) {
      map.setCenter(pins[0].position)
      map.setZoom(SINGLE_PIN_ZOOM)
      return
    }
    const bounds = new google.maps.LatLngBounds()
    pins.forEach(({ position }) => bounds.extend(position))
    map.fitBounds(bounds)
  }, [map, pins])

  const choosePlace = (next: FoundPlace | null) => {
    setPlace(next)
    if (!next || !map) return
    map.panTo({ lat: next.lat, lng: next.lng })
    map.setZoom(PLACE_ZOOM)
  }

  const listGroupsInView = () => {
    const bounds = map?.getBounds()
    if (!bounds) return
    setInViewIds(new Set(pins.filter(({ position }) => bounds.contains(position)).map(({ group }) => group.id)))
  }

  return (
    <div className="tw:flex tw:h-screen tw:flex-col tw:text-gray-900">
      <div className="tw:p-3">
        <LocationSearch
          label={t('search.label')}
          placeholder={t('search.placeholder')}
          unavailableMessage={t('search.unavailable')}
          failedMessage={t('search.failed')}
          country={country}
          googleMapsApiKey={apiKey}
          place={place}
          onPlaceChange={choosePlace}
          stacked={stacked}
        />
      </div>
      <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:md:grid tw:md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] tw:md:overflow-hidden">
        <div role="region" aria-label={t('map_label')} className="tw:h-[400px] tw:md:order-2 tw:md:h-full">
          {mapOptions ? (
            <GoogleMap
              mapContainerClassName="tw:h-full tw:w-full"
              center={START_CENTER}
              zoom={START_ZOOM}
              options={mapOptions}
              onLoad={setMap}
              onIdle={listGroupsInView}
            >
              <GoogleMarkerClusterer options={{}}>
                {(clusterer) => (
                  <>
                    {pins.map(({ group, position }) => (
                      <MarkerF
                        key={group.id}
                        clusterer={clusterer}
                        position={position}
                        title={group.name}
                        icon={PIN_ICON}
                        onClick={() => setSelectedId(group.id)}
                      />
                    ))}
                  </>
                )}
              </GoogleMarkerClusterer>
              {selected && (
                <InfoWindowF
                  position={selected.position}
                  options={{ pixelOffset: new google.maps.Size(0, -44), maxWidth: 340 }}
                  onCloseClick={() => setSelectedId(null)}
                >
                  <div className="tw:text-base tw:leading-6 tw:text-gray-900">
                    <GroupDetails group={selected.group} withExcerpt />
                  </div>
                </InfoWindowF>
              )}
            </GoogleMap>
          ) : (
            <div className="tw:h-full tw:w-full tw:animate-pulse tw:bg-gray-100 tw:motion-reduce:animate-none" />
          )}
        </div>
        <GroupList
          groups={inView}
          total={groups.length}
          onSelect={setSelectedId}
          className="tw:md:order-1 tw:md:overflow-y-auto"
        />
      </div>
    </div>
  )
}

type GroupsMapProps = {
  groups: MapGroup[]
  googleMapsApiKey: string | null
  country: string
}

export function GroupsMap({ groups, googleMapsApiKey, country }: GroupsMapProps) {
  const [mapFailed, setMapFailed] = useState(false)
  const failMap = useCallback(() => setMapFailed(true), [])

  if (!googleMapsApiKey || mapFailed) return <GroupList groups={groups} total={groups.length} />

  return <MapAndList groups={groups} apiKey={googleMapsApiKey} country={country} onError={failMap} />
}
